import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Calendar, 
  Clock, 
  MapPin,
  Video,
  User,
  ChevronLeft,
  ChevronRight,
  Filter,
  Search,
  Plus,
  Settings,
  CalendarCheck,
  Activity,
  TrendingUp,
  CheckCircle,
  AlertTriangle,
  Eye
} from "lucide-react";
import { 
  format, 
  addDays, 
  subDays, 
  startOfWeek, 
  endOfWeek, 
  startOfMonth, 
  endOfMonth,
  eachDayOfInterval,
  isSameDay,
  isSameMonth,
  isToday,
  parseISO,
  getHours,
  getMinutes,
  addWeeks,
  subWeeks,
  addMonths,
  subMonths
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { supabase } from '@/lib/supabase';
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

// Interface para as sessões baseada na view class_sessions_
interface SessionData {
  class_session_id: string;
  class_session_status: string;
  start_date: string;
  end_date: string;
  duration_minutes: number;
  teams_meeting_url?: string;
  event_calendar_id?: string;
  meeting_id?: string;
  mode: string;
  checked_out: boolean;
  custom_meeting_url?: string;
  meeting_url_type?: string;
  rescheduled_from?: string;
  class_id: string;
  display_id?: string;
  course_id?: string;
  name: string;
  family_name?: string;
  instructor_id?: string;
  instructor_name?: string;
  email?: string;
  location_address?: string;
  status: string;
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
  subscription_id?: string;
  state?: string;
  subscription_start_date?: string;
  subscription_end_date?: string;
  canceled_at?: string;
  org_name?: string;
  domain?: string;
  industry?: string;
  session_count?: number;
}

type ViewMode = 'day' | 'week' | 'month';

// Componente para renderizar cada slot de tempo
const TimeSlot: React.FC<{
  hour: number;
  sessions: SessionData[];
  onSessionClick: (session: SessionData) => void;
  isCompact?: boolean;
  isInDefaultRange?: boolean;
}> = ({ hour, sessions, onSessionClick, isCompact = false, isInDefaultRange = true }) => {
  const timeSlots = sessions.filter(session => {
    const sessionDate = parseISO(session.start_date);
    return getHours(sessionDate) === hour;
  });

  const minHeight = isCompact ? 'min-h-[45px]' : 'min-h-[60px]';
  const padding = isCompact ? 'p-2' : 'p-3';
  const opacity = isInDefaultRange ? 'opacity-100' : 'opacity-60';
  
  return (
    <div className={`${minHeight} border-b border-gray-100 bg-white/70 ${padding} hover:bg-gray-50/80 transition-colors ${opacity}`}>
      <div className="text-xs text-gray-500 mb-1">
        {format(new Date().setHours(hour, 0, 0, 0), 'HH:mm')}
      </div>
      {timeSlots.map((session) => (
        <div
          key={session.class_session_id}
          className={`
            rounded-md ${isCompact ? 'p-1.5' : 'p-2'} mb-1 cursor-pointer text-xs
            ${session.class_session_status === 'confirmed' 
              ? 'bg-green-100 text-green-800 border-l-4 border-green-500' 
              : session.class_session_status === 'expected'
              ? 'bg-blue-100 text-blue-800 border-l-4 border-blue-500'
              : session.class_session_status === 'cancelled'
              ? 'bg-red-100 text-red-800 border-l-4 border-red-500'
              : 'bg-orange-100 text-orange-800 border-l-4 border-orange-500'
            }
            hover:shadow-sm transition-shadow
          `}
          onClick={() => onSessionClick(session)}
        >
          <div className={`font-extrabold truncate ${isCompact ? 'text-xs' : ''}`}>{session.name}</div>
          <div className="text-xs opacity-75 truncate">
            {session.instructor_name || 'Sem instrutor'}
          </div>
          <div className="flex items-center gap-1 mt-1">
            {session.mode === 'onlineSync' ? (
              <Video className="h-3 w-3" />
            ) : (
              <MapPin className="h-3 w-3" />
            )}
            <Clock className="h-3 w-3" />
            <span>{session.duration_minutes}min</span>
          </div>
        </div>
      ))}
    </div>
  );
};

// Componente para o cabeçalho do calendário
const CalendarHeader: React.FC<{
  currentDate: Date;
  viewMode: ViewMode;
  onDateChange: (date: Date) => void;
  onViewModeChange: (mode: ViewMode) => void;
  statusFilter: string;
  onStatusFilterChange: (status: string) => void;
  searchTerm: string;
  onSearchTermChange: (term: string) => void;
}> = ({ 
  currentDate, 
  viewMode, 
  onDateChange, 
  onViewModeChange,
  statusFilter,
  onStatusFilterChange,
  searchTerm,
  onSearchTermChange
}) => {
  const goToPrevious = () => {
    if (viewMode === 'day') {
      onDateChange(subDays(currentDate, 1));
    } else if (viewMode === 'week') {
      onDateChange(subWeeks(currentDate, 1));
    } else {
      onDateChange(subMonths(currentDate, 1));
    }
  };

  const goToNext = () => {
    if (viewMode === 'day') {
      onDateChange(addDays(currentDate, 1));
    } else if (viewMode === 'week') {
      onDateChange(addWeeks(currentDate, 1));
    } else {
      onDateChange(addMonths(currentDate, 1));
    }
  };

  const getDateRangeText = () => {
    if (viewMode === 'day') {
      return format(currentDate, "EEEE, d 'de' MMMM 'de' yyyy", { locale: ptBR });
    } else if (viewMode === 'week') {
      const start = startOfWeek(currentDate, { weekStartsOn: 0 });
      const end = endOfWeek(currentDate, { weekStartsOn: 0 });
      return `${format(start, "d 'de' MMM", { locale: ptBR })} - ${format(end, "d 'de' MMM 'de' yyyy", { locale: ptBR })}`;
    } else {
      return format(currentDate, "MMMM 'de' yyyy", { locale: ptBR });
    }
  };

  return (
    <div className="flex items-center gap-3">
      {/* Navegação e Data */}
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-0.5">
          <Button variant="ghost" size="sm" onClick={goToPrevious} className="h-7 w-7 p-0 bg-gray-200 hover:bg-gray-300">
            <ChevronLeft className="h-3 w-3" />
          </Button>
          <Button variant="ghost" size="sm" onClick={goToNext} className="h-7 w-7 p-0 bg-gray-200 hover:bg-gray-300">
            <ChevronRight className="h-3 w-3" />
          </Button>
        </div>
        
        <Popover>
          <PopoverTrigger asChild>
            <Button 
              variant="ghost"
              className="h-7 px-2 text-xs font-extrabold lowercase hover:bg-gray-100 text-left justify-start"
            >
              {getDateRangeText()}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <CalendarComponent
              mode="single"
              selected={currentDate}
              onSelect={(date) => {
                if (date) {
                  onDateChange(date);
                }
              }}
              initialFocus
            />
          </PopoverContent>
        </Popover>
        
        <Button 
          variant="ghost" 
          size="sm"
          onClick={() => onDateChange(new Date())}
          className="h-7 px-2 text-xs font-extrabold bg-gray-200 hover:bg-gray-300"
        >
          Hoje
        </Button>
      </div>

      {/* Espaço flexível */}
      <div className="flex-1"></div>

      {/* Filtros compactos alinhados à direita */}
      <div className="flex items-center gap-2">
        <div className="relative w-60">
          <Search className="absolute left-2 top-1/2 h-3 w-3 text-gray-400 transform -translate-y-1/2" />
          <Input
            placeholder="Buscar sessões, instrutores..."
            value={searchTerm}
            onChange={(e) => onSearchTermChange(e.target.value)}
            className="pl-7 h-7 bg-white border-gray-200 focus:border-blue-500 focus:ring-blue-500 text-xs placeholder:text-xs"
          />
        </div>

        <div className="w-36">
          <Select value={statusFilter} onValueChange={onStatusFilterChange}>
            <SelectTrigger className="h-7 bg-white border-gray-200 focus:border-blue-500 focus:ring-blue-500 text-xs">
              <SelectValue placeholder="Status..." />
            </SelectTrigger>
            <SelectContent className="text-xs">
              <SelectItem value="all" className="text-xs">Todos</SelectItem>
              <SelectItem value="confirmed" className="text-xs">Confirmadas</SelectItem>
              <SelectItem value="expected" className="text-xs">Previstas</SelectItem>
              <SelectItem value="draft" className="text-xs">Rascunho</SelectItem>
              <SelectItem value="cancelled" className="text-xs">Canceladas</SelectItem>
            </SelectContent>
          </Select>
        </div>
      
        {/* Botões de visualização */}
        <div className="flex bg-gray-200 rounded p-0.5">
          <button
            onClick={() => onViewModeChange('day')}
            className={`flex items-center gap-1 px-2 py-1 text-xs font-extrabold rounded transition-colors ${
              viewMode === 'day'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            <Calendar className="h-3 w-3" />
            Dia
          </button>
          <button
            onClick={() => onViewModeChange('week')}
            className={`flex items-center gap-1 px-2 py-1 text-xs font-extrabold rounded transition-colors ${
              viewMode === 'week'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            <CalendarCheck className="h-3 w-3" />
            Semana
          </button>
          <button
            onClick={() => onViewModeChange('month')}
            className={`flex items-center gap-1 px-2 py-1 text-xs font-extrabold rounded transition-colors ${
              viewMode === 'month'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            <Calendar className="h-3 w-3" />
            Mês
          </button>
        </div>
      </div>
    </div>
  );
};

// Modal de detalhes da sessão inspirado no design moderno
const SessionDetailsModal: React.FC<{
  session: SessionData | null;
  isOpen: boolean;
  onClose: () => void;
}> = ({ session, isOpen, onClose }) => {
  if (!session) return null;

  const startDate = parseISO(session.start_date);
  const endDate = session.end_date ? parseISO(session.end_date) : null;

  const getStatusIcon = () => {
    switch (session.class_session_status) {
      case 'confirmed':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'expected':
        return <Clock className="h-4 w-4 text-blue-600" />;
      case 'cancelled':
        return <AlertTriangle className="h-4 w-4 text-red-600" />;
      default:
        return <Calendar className="h-4 w-4 text-orange-600" />;
    }
  };

  const getStatusText = () => {
    switch (session.class_session_status) {
      case 'confirmed':
        return 'Confirmada';
      case 'expected':
        return 'Prevista';
      case 'cancelled':
        return 'Cancelada';
      default:
        return 'Rascunho';
    }
  };

  const getModeIcon = () => {
    switch (session.mode) {
      case 'onlineSync':
        return <Video className="h-5 w-5 text-blue-600" />;
      case 'in-person':
        return <MapPin className="h-5 w-5 text-green-600" />;
      default:
        return <Calendar className="h-5 w-5 text-purple-600" />;
    }
  };

  const getModeText = () => {
    switch (session.mode) {
      case 'onlineSync':
        return 'Online';
      case 'in-person':
        return 'Main Branch Office';
      default:
        return 'Online Assíncrono';
    }
  };

    return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[680px] p-0 overflow-hidden border-0">
        {/* Header com fundo colorido */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                {getStatusIcon()}
                <span className="text-sm font-extrabold bg-white/20 px-2 py-1 rounded-full">
                  {getStatusText()}
                </span>
              </div>
              <h2 className="text-xl font-extrabold mb-1">{session.name}</h2>
              {session.family_name && (
                <p className="text-blue-100 text-sm">{session.family_name}</p>
              )}
            </div>
            
            {/* Avatares dos participantes */}
            <div className="flex items-center gap-2">
              <div className="flex -space-x-2">
                {session.instructor_name && (
                  <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center border-2 border-white">
                    <User className="h-4 w-4 text-white" />
                  </div>
                )}
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center border-2 border-white">
                  <span className="text-xs font-extrabold">+{session.session_count || 1}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Conteúdo principal em layout horizontal */}
        <div className="p-6">
          <div className="grid grid-cols-3 gap-6">
            {/* Coluna 1: Data, Hora e Localização */}
            <div className="space-y-4">
              {/* Data e Hora */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="h-4 w-4 text-gray-400" />
                  <span className="text-sm font-extrabold text-gray-600">Horário</span>
                </div>
                <div className="space-y-1">
                  <p className="text-lg font-extrabold text-gray-900">
                    {format(startDate, "HH:mm")} → {endDate ? format(endDate, "HH:mm") : `${format(startDate, "HH:mm")} (${session.duration_minutes}min)`}
                  </p>
                  <p className="text-sm font-extrabold text-gray-700">
                    {format(startDate, "dd MMM yyyy", { locale: ptBR })}
                  </p>
                  <p className="text-sm text-gray-500">
                    {format(startDate, "EEEE", { locale: ptBR })}
                  </p>
                </div>
              </div>

              {/* Localização */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  {getModeIcon()}
                  <span className="text-sm font-extrabold text-gray-600">Localização</span>
                </div>
                <div className="space-y-1">
                  <p className="font-extrabold text-gray-900">{getModeText()}</p>
                  {session.location_address && (
                    <p className="text-sm text-gray-500">{session.location_address}</p>
                  )}
                  {session.mode === 'onlineSync' && (
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-xs text-green-600 font-extrabold">Online</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Coluna 2: Instrutor e Organização */}
            <div className="space-y-4">
              {/* Instrutor */}
              {session.instructor_name && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <User className="h-4 w-4 text-gray-400" />
                    <span className="text-sm font-extrabold text-gray-600">Instrutor</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center">
                      <User className="h-4 w-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-extrabold text-gray-900 text-sm">{session.instructor_name}</p>
                      {session.email && (
                        <p className="text-xs text-gray-500">{session.email}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Organização */}
              {session.org_name && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <span className="text-sm font-extrabold text-gray-600">Organização</span>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="font-extrabold text-gray-900 text-sm">{session.org_name}</p>
                    {session.domain && (
                      <p className="text-xs text-gray-500">{session.domain}</p>
                    )}
                    {session.industry && (
                      <p className="text-xs text-gray-400 mt-1">{session.industry}</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Coluna 3: Score Card e Ações */}
            <div className="space-y-4">
              {/* Score Card Section */}
              {session.class_session_status === 'confirmed' && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <CalendarCheck className="h-4 w-4 text-gray-400" />
                    <span className="text-sm font-extrabold text-gray-600">Score Card</span>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-sm text-green-800">Sessão confirmada e pronta para execução</p>
                  </div>
                </div>
              )}

              {/* Botão de Ingressar (para aulas online) */}
              {session.mode === 'onlineSync' && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Video className="h-4 w-4 text-gray-400" />
                    <span className="text-sm font-extrabold text-gray-600">Aula Online</span>
                  </div>
                  <Button 
                    className="w-full bg-green-600 hover:bg-green-700 text-white"
                    size="default"
                  >
                    <Video className="h-4 w-4 mr-2" />
                    Ingressar na Aula Online
                  </Button>
                  {session.teams_meeting_url && (
                    <p className="text-xs text-gray-500 mt-1">
                      Link disponível 15 minutos antes do início
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer com botões de ação */}
        <div className="bg-gray-50 px-6 py-4 border-t">
          <div className="flex justify-between items-center">
            <Button variant="ghost" onClick={onClose} className="text-gray-600">
              Fechar
            </Button>
            <div className="flex gap-2">
              <Button 
                variant="outline"
                className="border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                <Settings className="h-4 w-4 mr-2" />
                Editar
              </Button>
              <Button 
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700"
              >
                <Eye className="h-4 w-4 mr-2" />
                Ver Detalhes
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function SessionsCalendar() {
  const [sessions, setSessions] = useState<SessionData[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<ViewMode>('week');
  const [selectedSession, setSelectedSession] = useState<SessionData | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    confirmed: 0,
    expected: 0,
    cancelled: 0,
    today: 0
  });

  // Buscar sessões da view class_sessions_
  const fetchSessions = async () => {
    try {
      setLoading(true);
      
      let startDate: Date;
      let endDate: Date;
      
      if (viewMode === 'day') {
        startDate = new Date(currentDate);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(currentDate);
        endDate.setHours(23, 59, 59, 999);
      } else if (viewMode === 'week') {
        startDate = startOfWeek(currentDate, { weekStartsOn: 0 });
        endDate = endOfWeek(currentDate, { weekStartsOn: 0 });
      } else {
        startDate = startOfMonth(currentDate);
        endDate = endOfMonth(currentDate);
      }

      let query = supabase
        .from('class_sessions_')
        .select('*')
        .gte('start_date', startDate.toISOString())
        .lte('start_date', endDate.toISOString())
        .order('start_date', { ascending: true });

      if (statusFilter !== 'all') {
        query = query.eq('class_session_status', statusFilter);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Erro ao buscar sessões:', error);
        return;
      }

      setSessions(data || []);
      
      // Calcular estatísticas
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      const newStats = {
        total: data?.length || 0,
        confirmed: data?.filter(s => s.class_session_status === 'confirmed').length || 0,
        expected: data?.filter(s => s.class_session_status === 'expected').length || 0,
        cancelled: data?.filter(s => s.class_session_status === 'cancelled').length || 0,
        today: data?.filter(s => {
          const sessionDate = parseISO(s.start_date);
          return sessionDate >= today && sessionDate < tomorrow;
        }).length || 0
      };
      
      setStats(newStats);
    } catch (error) {
      console.error('Erro:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, [currentDate, viewMode, statusFilter]);

  // Filtrar sessões por termo de busca
  const filteredSessions = useMemo(() => {
    if (!searchTerm) return sessions;
    
    return sessions.filter(session => 
      session.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      session.instructor_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      session.org_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [sessions, searchTerm]);

  const handleSessionClick = (session: SessionData) => {
    setSelectedSession(session);
    setIsModalOpen(true);
  };

     // Renderizar visualização por dia
     const renderDayView = () => {
    const allHours = Array.from({ length: 17 }, (_, i) => i + 7); // 7h às 23h
    const defaultStartHour = 8;
    const defaultEndHour = 19;
    
    return (
      <div className="border border-gray-100 rounded-lg overflow-hidden bg-gray-50/30">
        <div className="border-b border-gray-100 bg-white/50 px-3 py-2">
          <h3 className="text-base font-extrabold text-gray-900 flex items-center gap-2">
            <Calendar className="h-4 w-4 text-blue-600" />
            {format(currentDate, "EEEE, d 'de' MMMM", { locale: ptBR })}
          </h3>
        </div>
        <div className="h-96 overflow-y-auto">
          {allHours.map(hour => {
            const isInDefaultRange = hour >= defaultStartHour && hour <= defaultEndHour;
            return (
              <TimeSlot
                key={hour}
                hour={hour}
                sessions={filteredSessions.filter(session => {
                  const sessionDate = parseISO(session.start_date);
                  return isSameDay(sessionDate, currentDate);
                })}
                onSessionClick={handleSessionClick}
                isCompact={true}
                isInDefaultRange={isInDefaultRange}
              />
            );
          })}
        </div>
      </div>
    );
  };

     // Renderizar visualização por semana
     const renderWeekView = () => {
    const days = eachDayOfInterval({
      start: startOfWeek(currentDate, { weekStartsOn: 0 }),
      end: endOfWeek(currentDate, { weekStartsOn: 0 })
    });

    const allHours = Array.from({ length: 17 }, (_, i) => i + 7); // 7h às 23h
    const defaultStartHour = 8;
    const defaultEndHour = 19;

    return (
      <div className="border border-gray-100 rounded-lg overflow-hidden bg-gray-50/30">
        <div className="grid grid-cols-8 border-b bg-white/50">
          <div className="p-1.5 text-xs font-extrabold text-gray-500">Horário</div>
          {days.map(day => (
            <div key={day.toISOString()} className="p-1.5 text-center border-l">
              <div className={`text-xs font-extrabold ${isToday(day) ? 'text-blue-600' : 'text-gray-700'}`}>
                {format(day, 'EEE', { locale: ptBR })}
              </div>
              <div className={`text-sm font-extrabold ${isToday(day) ? 'text-blue-600' : 'text-gray-900'}`}>
                {format(day, 'd')}
              </div>
            </div>
          ))}
        </div>
        
        <div className="h-96 overflow-y-auto">
          {allHours.map(hour => {
            const isInDefaultRange = hour >= defaultStartHour && hour <= defaultEndHour;
            const opacity = isInDefaultRange ? 'opacity-100' : 'opacity-60';
            
            return (
              <div key={hour} className={`grid grid-cols-8 border-b min-h-[40px] ${opacity}`}>
                <div className="p-1.5 text-xs text-gray-500 border-r bg-gray-50/50">
                  {format(new Date().setHours(hour, 0, 0, 0), 'HH:mm')}
                </div>
                {days.map(day => (
                  <div key={`${hour}-${day.toISOString()}`} className="border-l p-0.5">
                    {filteredSessions
                      .filter(session => {
                        const sessionDate = parseISO(session.start_date);
                        return isSameDay(sessionDate, day) && getHours(sessionDate) === hour;
                      })
                      .map(session => (
                        <div
                          key={session.class_session_id}
                          className={`
                            rounded-sm p-1 mb-0.5 cursor-pointer text-xs
                            ${session.class_session_status === 'confirmed' 
                              ? 'bg-green-100 text-green-800' 
                              : session.class_session_status === 'expected'
                              ? 'bg-blue-100 text-blue-800'
                              : session.class_session_status === 'cancelled'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-orange-100 text-orange-800'
                            }
                            hover:shadow-sm transition-shadow
                          `}
                          onClick={() => handleSessionClick(session)}
                        >
                          <div className="font-extrabold truncate text-xs leading-tight">{session.name}</div>
                          <div className="text-xs opacity-75">{session.duration_minutes}min</div>
                        </div>
                      ))}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

     // Renderizar visualização por mês
   const renderMonthView = () => {
     const days = eachDayOfInterval({
       start: startOfWeek(startOfMonth(currentDate), { weekStartsOn: 0 }),
       end: endOfWeek(endOfMonth(currentDate), { weekStartsOn: 0 })
     });

     return (
       <div className="border border-gray-100 rounded-lg overflow-hidden bg-gray-50/30">
        <div className="grid grid-cols-7 border-b">
                      {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(day => (
              <div key={day} className="p-2 text-center text-xs font-extrabold text-gray-500 border-r last:border-r-0">
                {day}
              </div>
            ))}
        </div>
        
        <div className="grid grid-cols-7">
          {days.map(day => {
            const daySessions = filteredSessions.filter(session =>
              isSameDay(parseISO(session.start_date), day)
            );
            
            return (
              <div key={day.toISOString()} className="border-r border-b last:border-r-0 p-1.5 min-h-[80px] hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => setCurrentDate(day)}>
                <div className={`text-xs font-extrabold mb-1 ${
                  isToday(day) 
                    ? 'text-blue-600' 
                    : !isSameMonth(day, currentDate)
                    ? 'text-gray-400'
                    : 'text-gray-900'
                }`}>
                  {format(day, 'd')}
                </div>
                
                <div className="space-y-0.5">
                  {daySessions.slice(0, 2).map(session => (
                    <div
                      key={session.class_session_id}
                      className={`
                        rounded-sm p-0.5 cursor-pointer text-xs leading-tight
                        ${session.class_session_status === 'confirmed' 
                          ? 'bg-green-100 text-green-800' 
                          : session.class_session_status === 'expected'
                          ? 'bg-blue-100 text-blue-800'
                          : session.class_session_status === 'cancelled'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-orange-100 text-orange-800'
                        }
                        hover:shadow-sm transition-shadow
                      `}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSessionClick(session);
                      }}
                    >
                      <div className="font-extrabold truncate">{session.name}</div>
                      <div className="text-xs opacity-75">
                        {format(parseISO(session.start_date), 'HH:mm')}
                      </div>
                    </div>
                  ))}
                  {daySessions.length > 2 && (
                    <div className="text-xs text-gray-500 px-0.5">
                      +{daySessions.length - 2} mais
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 pl-3 lg:pl-4">
      <Helmet>
        <title>CSM | Trivium - Calendário de Sessões</title>
      </Helmet>

      {/* Modern Header with Gradient Background */}
      <div className="relative overflow-hidden" style={{ backgroundColor: '#1F1746' }}>
        {/* SVG Background Element */}
        <div className="absolute inset-0 pointer-events-none">
          <svg 
            className="opacity-30"
            width="1690"
            height="1026.5433"
            viewBox="0 0 826.54 502.06" 
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="xMidYMid slice"
          >
            <g transform="scale(1.1) translate(-70, -315)">
              <path d="M0 0h826.54v502.06H0z" fill="none"/>
              <path d="M0 0h826.54v502.06H0z" fill="url(#a)"/>
              <defs>
                <linearGradient id="a" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#6366f1" stopOpacity=".1"/>
                  <stop offset="100%" stopColor="#8b5cf6" stopOpacity=".1"/>
                </linearGradient>
              </defs>
            </g>
          </svg>
        </div>
        <div className="absolute inset-0 bg-black/20" />
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-white/5" />
        
        <div className="relative container mx-auto px-6 py-6 pb-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                  <CalendarCheck className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-extrabold text-white">Calendário de Sessões</h1>
                  <p className="text-blue-100 text-base pb-2">
                    Visualize e gerencie suas sessões de classes e cursos
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button 
                variant="secondary" 
                size="default" 
                className="backdrop-blur-sm bg-white/20 border-white/30 text-white hover:bg-white/30 transition-all duration-200"
              >
                <Settings className="h-4 w-4 mr-2" />
                Configurar
              </Button>
              <Button 
                size="default" 
                className="bg-white text-gray-900 hover:bg-gray-100 shadow-lg"
              >
                <Plus className="h-4 w-4 mr-2" />
                Nova Sessão
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 -mt-4 relative z-10">

        {/* Enhanced Summary Cards with Better Visual Hierarchy */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-6 mb-8">
          <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-blue-50 to-white">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-blue-100 rounded-md">
                    <Calendar className="h-4 w-4 text-blue-600" />
                  </div>
                  <span className="text-sm font-extrabold text-gray-600">Total de Sessões</span>
                </div>
                <div className="text-2xl font-extrabold text-gray-900">
                  {stats.total}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-500">
                  No período selecionado
                </div>
                <div className="flex items-center gap-1">
                  <TrendingUp className="h-3 w-3 text-blue-500" />
                  <span className="text-xs text-green-600 font-extrabold">+5%</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-green-50 to-white">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-green-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-green-100 rounded-md">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  </div>
                  <span className="text-sm font-extrabold text-gray-600">Confirmadas</span>
                </div>
                <div className="text-2xl font-extrabold text-gray-900">
                  {stats.confirmed}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-500">
                  {stats.total > 0 ? Math.round((stats.confirmed / stats.total) * 100) : 0}% do total
                </div>
                <div className="flex items-center gap-1">
                  <Activity className="h-3 w-3 text-green-500" />
                  <span className="text-xs text-green-600 font-extrabold">+8%</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-orange-50 to-white">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-orange-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-orange-100 rounded-md">
                    <Clock className="h-4 w-4 text-orange-600" />
                  </div>
                  <span className="text-sm font-extrabold text-gray-600">Previstas</span>
                </div>
                <div className="text-2xl font-extrabold text-gray-900">
                  {stats.expected}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-500">
                  Aguardando confirmação
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="h-3 w-3 text-orange-500" />
                  <span className="text-xs text-orange-600 font-extrabold">+2%</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-red-50 to-white">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-red-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-red-100 rounded-md">
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                  </div>
                  <span className="text-sm font-extrabold text-gray-600">Canceladas</span>
                </div>
                <div className="text-2xl font-extrabold text-gray-900">
                  {stats.cancelled}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-500">
                  Sessões canceladas
                </div>
                <div className="flex items-center gap-1">
                  <TrendingUp className="h-3 w-3 text-red-500 rotate-180" />
                  <span className="text-xs text-red-600 font-extrabold">-1%</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-purple-50 to-white">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-purple-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-purple-100 rounded-md">
                    <CalendarCheck className="h-4 w-4 text-purple-600" />
                  </div>
                  <span className="text-sm font-extrabold text-gray-600">Hoje</span>
                </div>
                <div className="text-2xl font-extrabold text-gray-900">
                  {stats.today}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-500">
                  Sessões de hoje
                </div>
                <div className="flex items-center gap-1">
                  <Eye className="h-3 w-3 text-purple-500" />
                  <span className="text-xs text-purple-600 font-extrabold">Hoje</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Enhanced Main Content with Modern Design */}
        <Card className="border shadow-sm bg-white/80 backdrop-blur-sm">
          <CardContent className="p-6">
            {/* Combined Header and Filters */}
            <div className="mb-6">
              <CalendarHeader
                currentDate={currentDate}
                viewMode={viewMode}
                onDateChange={setCurrentDate}
                onViewModeChange={setViewMode}
                statusFilter={statusFilter}
                onStatusFilterChange={setStatusFilter}
                searchTerm={searchTerm}
                onSearchTermChange={setSearchTerm}
              />
            </div>

            {/* Calendar Content */}
            {loading ? (
              <div className="flex items-center justify-center h-96">
                <div className="text-center space-y-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center mx-auto">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div>
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-lg font-extrabold text-gray-900">Carregando sessões</h3>
                    <p className="text-gray-500">Aguarde enquanto buscamos as informações...</p>
                  </div>
                </div>
              </div>
            ) : (
              <div>
                {viewMode === 'day' && renderDayView()}
                {viewMode === 'week' && renderWeekView()}
                {viewMode === 'month' && renderMonthView()}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Modal de detalhes */}
        <SessionDetailsModal
          session={selectedSession}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
      </div>
      
      {/* Bottom Spacing */}
      <div className="h-16" />
    </div>
  );
}
